"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TrendingUp, Search, Target, ShieldAlert, ArrowRight, MapPin, CircleCheck as CheckCircle2, ChartBar as BarChart3, Zap, Globe as Globe2, Building2, ArrowUpRight } from 'lucide-react';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const trendData = [{
  month: "Jan",
  searchVolume: 12400,
  mobileRatio: 64
}, {
  month: "Feb",
  searchVolume: 13100,
  mobileRatio: 66
}, {
  month: "Mar",
  searchVolume: 14800,
  mobileRatio: 68
}, {
  month: "Apr",
  searchVolume: 15200,
  mobileRatio: 71
}, {
  month: "May",
  searchVolume: 16900,
  mobileRatio: 73
}, {
  month: "Jun",
  searchVolume: 18500,
  mobileRatio: 75
}, {
  month: "Jul",
  searchVolume: 19800,
  mobileRatio: 78
}];
const localKeywords = [{
  id: 1,
  keyword: "SEO services Swansea",
  volume: "1,100/mo",
  intent: "High Commercial",
  difficulty: "Medium",
  opportunity: "High map-pack expansion potential"
}, {
  id: 2,
  keyword: "Local SEO consultant Swansea",
  volume: "480/mo",
  intent: "Transactional",
  difficulty: "Low-Medium",
  opportunity: "Direct lead generation focus"
}, {
  id: 3,
  keyword: "B2B digital marketing Swansea",
  volume: "720/mo",
  intent: "Commercial",
  difficulty: "Medium",
  opportunity: "High average deal value focus"
}, {
  id: 4,
  keyword: "Ecommerce SEO specialist South Wales",
  volume: "950/mo",
  intent: "Transactional",
  difficulty: "Medium",
  opportunity: "Regional shopify & magento queries"
}];
const competitorWeaknesses = [{
  id: 1,
  title: "Incomplete Local Schema Architecture",
  description: "Over 68% of local Swansea business domains lack geotargeted JSON-LD structured markup, leaving Map Pack rankings on the table.",
  impact: "High Ranking Gap"
}, {
  id: 2,
  title: "Unoptimized Mobile User Experience",
  description: "With 78% of local searches happening on mobile devices, competitors frequently fail Core Web Vitals targets in Swansea.",
  impact: "Conversion Loss"
}, {
  id: 3,
  title: "Generic Agency Content Templates",
  description: "Local competitors rely on duplicated boilerplate content instead of authentic, location-relevant context tailored to South Wales.",
  impact: "Trust Deficit"
}];
const neighboringCities = [{
  id: 1,
  name: "Gloucester",
  path: "/LocalSeoConsultantGloucesterPage",
  distance: "95 miles",
  focus: "Commercial & Regional Hub"
}, {
  id: 2,
  name: "Cheltenham",
  path: "/LocalSeoConsultantCheltenhamPage",
  distance: "105 miles",
  focus: "B2B & Professional Services"
}, {
  id: 3,
  name: "Plymouth",
  path: "/LocalSeoConsultantPlymouthPage",
  distance: "140 miles",
  focus: "Maritime & Local Services"
}];
export default function Swanseamarket() {
  const shouldReduceMotion = useReducedMotion();
  const [activeTab, setActiveTab] = useState<"keywords" | "competitors">("keywords");
  return <section id="swanseamarketanalysis" data-nav="light" className="py-20 md:py-28 bg-background text-foreground relative overflow-hidden">
      <div className="absolute top-0 right-0 -z-10 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 -z-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs uppercase tracking-wider bg-secondary text-secondary-foreground font-medium">
            <MapPin className="w-3.5 h-3.5 text-primary" />
            <span>Swansea SEO Market Intelligence</span>
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-foreground tracking-tight mb-6 font-default">
            Swansea Search Growth &amp; Organic Market Analysis
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed font-default">
            I analyzed local Swansea search behavior to map out high-intent keywords, competitor vulnerabilities, and immediate organic growth opportunities for businesses operating in South Wales.
          </p>
        </div>

        <Card className="mb-12 border border-border shadow-md bg-card">
          <CardHeader className="pb-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <CardTitle className="text-xl sm:text-2xl font-semibold text-card-foreground flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary" />
                  Local Swansea Search Volume Trajectory
                </CardTitle>
                <CardDescription className="text-muted-foreground mt-1">
                  Verified 12-month query growth across Swansea local services and B2B sectors.
                </CardDescription>
              </div>
              <div className="flex items-center gap-3 bg-muted/50 p-2 rounded-lg border border-border">
                <div className="flex items-center gap-2 text-xs font-medium text-foreground">
                  <span className="w-3 h-3 rounded-full bg-primary inline-block" />
                  Total Search Volume
                </div>
                <Separator orientation="vertical" className="h-4" />
                <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                  <span className="w-3 h-3 rounded-full bg-accent inline-block" />
                  Mobile Query Ratio (%)
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-72 w-full pt-4">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendData} margin={{
                top: 10,
                right: 10,
                left: -20,
                bottom: 0
              }}>
                  <defs>
                    <linearGradient id="colorVolume" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--color-primary)" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="var(--color-primary)" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="month" stroke="var(--color-muted-foreground)" fontSize={12} tickLine={false} />
                  <YAxis stroke="var(--color-muted-foreground)" fontSize={12} tickLine={false} />
                  <Tooltip contentStyle={{
                  backgroundColor: "var(--color-card)",
                  borderColor: "var(--color-border)",
                  borderRadius: "0.5rem",
                  color: "var(--color-card-foreground)",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)"
                }} />
                  <Area type="monotone" dataKey="searchVolume" stroke="var(--color-primary)" strokeWidth={2} fillOpacity={1} fill="url(#colorVolume)" name="Monthly Search Queries" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6 pt-6 border-t border-border">
              <div className="p-4 rounded-lg bg-background border border-border">
                <div className="text-xs text-muted-foreground font-medium mb-1">Local Growth Rate</div>
                <div className="text-2xl font-bold text-foreground flex items-center gap-1">
                  <span>+59.6%</span>
                  <TrendingUp className="w-4 h-4 text-emerald-600" />
                </div>
                <div className="text-xs text-muted-foreground mt-1">Year-over-year organic queries</div>
              </div>
              <div className="p-4 rounded-lg bg-background border border-border">
                <div className="text-xs text-muted-foreground font-medium mb-1">Mobile Local Intent</div>
                <div className="text-2xl font-bold text-foreground">78%</div>
                <div className="text-xs text-muted-foreground mt-1">Searches made on mobile devices</div>
              </div>
              <div className="p-4 rounded-lg bg-background border border-border">
                <div className="text-xs text-muted-foreground font-medium mb-1">Map Pack Conversion</div>
                <div className="text-2xl font-bold text-foreground">3.4x</div>
                <div className="text-xs text-muted-foreground mt-1">Higher CTR for top 3 Map listings</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="mb-16">
          <div className="flex justify-center mb-8">
            <div className="inline-flex p-1 rounded-xl bg-muted border border-border">
              <Button variant={activeTab === "keywords" ? "default" : "ghost"} size="sm" onClick={() => setActiveTab("keywords")} className="rounded-lg text-sm font-medium transition-all">
                <Search className="w-4 h-4 mr-2" />
                High-Intent Local Keywords
              </Button>
              <Button variant={activeTab === "competitors" ? "default" : "ghost"} size="sm" onClick={() => setActiveTab("competitors")} className="rounded-lg text-sm font-medium transition-all">
                <ShieldAlert className="w-4 h-4 mr-2" />
                Competitor Vulnerabilities
              </Button>
            </div>
          </div>

          {activeTab === "keywords" && <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3
        }} className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {localKeywords.map((kw, i) => <div data-index={i} key={kw.id}>
                  <Card className="h-full border border-border bg-card hover:shadow-md transition-[box-shadow,border-color]">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between gap-3">
                        <CardTitle className="text-lg font-semibold text-foreground">
                          {kw.keyword}
                        </CardTitle>
                        <Badge variant="outline" className="text-xs font-normal border-primary/30 text-primary bg-primary/5">
                          {kw.intent}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between text-sm py-2 border-y border-border">
                        <span className="text-muted-foreground">Estimated Search Volume:</span>
                        <span className="font-semibold text-foreground font-mono">{kw.volume}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Competition Difficulty:</span>
                        <span className="font-medium text-foreground">{kw.difficulty}</span>
                      </div>
                      <div className="p-3 rounded-md bg-secondary/50 border border-border text-xs text-foreground flex items-start gap-2">
                        <Zap className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                        <span><strong>Strategic Value:</strong> {kw.opportunity}</span>
                      </div>
                    </CardContent>
                  </Card>
                </div>)}
            </motion.div>}

          {activeTab === "competitors" && <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3
        }} className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {competitorWeaknesses.map((comp, i) => <div data-index={i} key={comp.id}>
                  <Card className="h-full border border-border bg-card hover:shadow-md transition-[box-shadow,border-color]">
                    <CardHeader className="pb-3">
                      <Badge variant="destructive" className="w-fit text-xs font-normal mb-2">
                        {comp.impact}
                      </Badge>
                      <CardTitle className="text-base font-semibold text-foreground">
                        {comp.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {comp.description}
                      </p>
                    </CardContent>
                  </Card>
                </div>)}
            </motion.div>}
        </div>

        <div className="p-8 sm:p-10 rounded-2xl bg-primary text-primary-foreground shadow-xl mb-16 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-accent/20 rounded-full blur-2xl pointer-events-none" />
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8 space-y-4">
              <Badge className="bg-primary-foreground/10 text-primary-foreground border-primary-foreground/20 text-xs">
                Direct Consultant Execution
              </Badge>
              <h3 className="text-2xl sm:text-3xl font-semibold tracking-tight font-default">
                My Swansea SEO Action Plan
              </h3>
              <p className="text-primary-foreground/90 text-sm sm:text-base leading-relaxed">
                When you work directly with me, I bypass generic agency packages. We optimize your local entity signals, structure hyper-targeted Swansea landing pages, build authoritative local citations, and resolve technical performance bottlenecks.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <div className="flex items-center gap-2 text-xs sm:text-sm text-primary-foreground/90">
                  <CheckCircle2 className="w-4 h-4 text-accent shrink-0" />
                  <span>Google Map Pack Geo-targeting</span>
                </div>
                <div className="flex items-center gap-2 text-xs sm:text-sm text-primary-foreground/90">
                  <CheckCircle2 className="w-4 h-4 text-accent shrink-0" />
                  <span>Local Schema &amp; Entity Markup</span>
                </div>
                <div className="flex items-center gap-2 text-xs sm:text-sm text-primary-foreground/90">
                  <CheckCircle2 className="w-4 h-4 text-accent shrink-0" />
                  <span>High-Intent Conversion UX</span>
                </div>
                <div className="flex items-center gap-2 text-xs sm:text-sm text-primary-foreground/90">
                  <CheckCircle2 className="w-4 h-4 text-accent shrink-0" />
                  <span>Transparent Lead Attribution</span>
                </div>
              </div>
            </div>
            <div className="lg:col-span-4 flex flex-col items-start lg:items-end justify-center">
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-md font-medium text-sm w-full sm:w-auto">
                <Link to="/ContactPage">
                  <span>Book Free Consultation</span>
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
            </div>
          </div>
        </div>

        <div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h3 className="text-xl sm:text-2xl font-semibold text-foreground font-default">
                Regional SEO Coverage &amp; Neighboring Locations
              </h3>
              <p className="text-muted-foreground text-sm mt-1">
                Explore tailored local SEO strategies across nearby commercial centers and key regional hubs.
              </p>
            </div>
            <Button variant="outline" size="sm" asChild className="shrink-0">
              <Link to="/LocalSEOPage">
                <span>View All Local SEO Locations</span>
                <ArrowUpRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {neighboringCities.map((city, i) => <div data-index={i} key={city.id}>
                <Card className="h-full border border-border bg-card hover:border-primary/50 transition-[border-color,box-shadow]">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base font-semibold text-foreground">
                        Local SEO {city.name}
                      </CardTitle>
                      <Badge variant="secondary" className="text-xs font-normal">
                        {city.distance}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-xs text-muted-foreground">
                      Focus: <strong className="text-foreground font-medium">{city.focus}</strong>
                    </p>
                    <Separator />
                    <Button variant="ghost" size="sm" asChild className="w-full justify-between px-0 hover:bg-transparent text-primary hover:text-primary/80">
                      <Link to={city.path}>
                        <span>Explore {city.name} SEO Strategy</span>
                        <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}